import { Link, useLocation } from "wouter";
import { ReactNode, useState, useEffect } from "react";
import { Users, BarChart2, Settings, LogOut, Menu, X, ShieldCheck, LifeBuoy, BookOpen, Globe, Mail } from "lucide-react";
import { useAuth } from "@/hooks/use-auth";
import { Button } from "@/components/ui/button";
import { useAdminSupportNotifications } from "@/hooks/use-admin-support-notifications";
import { useIsMobile } from "@/hooks/use-mobile";

const navItems = [
  { href: "/admin", label: "User Analytics", icon: BarChart2, exact: true },
  { href: "/admin/analytics/public", label: "Public Analytics", icon: Globe },
  { href: "/admin/users", label: "Users", icon: Users },
  { href: "/admin/support", label: "Support", icon: LifeBuoy },
  { href: "/admin/email", label: "Email Users", icon: Mail },
  { href: "/admin/blog", label: "Blog", icon: BookOpen },
  { href: "/admin/settings", label: "System Settings", icon: Settings },
];

export function AdminLayout({ children }: { children: ReactNode }) {
  const [location] = useLocation();
  const { user, logout } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const { totalUnread } = useAdminSupportNotifications();
  const isMobile = useIsMobile();

  useEffect(() => {
    if (!isMobile) setMobileOpen(false);
  }, [isMobile]);

  const isActive = (href: string, exact?: boolean) => {
    if (exact) return location === href;
    return location.startsWith(href);
  };

  const navLinkStyle = (href: string, exact?: boolean) => ({
    display: "flex",
    alignItems: "center",
    gap: "0.75rem",
    padding: "0.625rem 0.75rem",
    borderRadius: "0.375rem",
    cursor: "pointer",
    textDecoration: "none",
    transition: "background 0.15s, color 0.15s",
    backgroundColor: isActive(href, exact) ? "hsl(var(--sidebar-accent))" : "transparent",
    color: isActive(href, exact) ? "hsl(var(--sidebar-accent-foreground))" : "hsl(var(--sidebar-foreground) / 0.7)",
    fontWeight: isActive(href, exact) ? 500 : 400,
    fontSize: "0.9375rem",
    justifyContent: "space-between",
  });

  const SidebarContent = () => (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <div style={{ padding: "1.25rem 0.75rem 1rem" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "1.5rem", paddingLeft: "0.25rem" }}>
          <ShieldCheck style={{ width: "1.5rem", height: "1.5rem", color: "hsl(var(--primary))" }} />
          <span style={{ fontWeight: 700, fontSize: "1.125rem", color: "hsl(var(--sidebar-foreground))" }}>Admin Panel</span>
        </div>

        <nav style={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
          {navItems.map(({ href, label, icon: Icon, exact }) => (
            <Link key={href} href={href}>
              <div
                style={navLinkStyle(href, exact)}
                onClick={() => setMobileOpen(false)}
              >
                <span style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
                  <Icon style={{ width: "1.25rem", height: "1.25rem", flexShrink: 0 }} />
                  {label}
                </span>
                {href === "/admin/support" && totalUnread > 0 && (
                  <span style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    minWidth: "1.25rem",
                    height: "1.25rem",
                    padding: "0 0.25rem",
                    borderRadius: "9999px",
                    background: "#ef4444",
                    color: "#fff",
                    fontSize: "0.6875rem",
                    fontWeight: 700,
                    flexShrink: 0,
                  }}>
                    {totalUnread > 99 ? "99+" : totalUnread}
                  </span>
                )}
              </div>
            </Link>
          ))}
        </nav>

        <div style={{ marginTop: "1.5rem", paddingTop: "1rem", borderTop: "1px solid hsl(var(--sidebar-border))" }}>
          <Link href="/">
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.75rem",
                padding: "0.625rem 0.75rem",
                borderRadius: "0.375rem",
                cursor: "pointer",
                color: "hsl(var(--sidebar-foreground) / 0.7)",
                fontSize: "0.9375rem",
              }}
              onClick={() => setMobileOpen(false)}
            >
              Back to App
            </div>
          </Link>
        </div>
      </div>

      <div style={{ marginTop: "auto", padding: "1rem 0.75rem", borderTop: "1px solid hsl(var(--sidebar-border))" }}>
        {user && (
          <div>
            <p style={{ fontSize: "0.75rem", color: "hsl(var(--sidebar-foreground) / 0.5)", marginBottom: "0.375rem", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", paddingLeft: "0.75rem" }}>
              {user.email}
            </p>
            <button
              onClick={async () => { await logout(); }}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
                fontSize: "0.8125rem",
                color: "hsl(var(--sidebar-foreground) / 0.6)",
                background: "none",
                border: "none",
                cursor: "pointer",
                padding: "0.375rem 0.75rem",
                width: "100%",
                borderRadius: "0.375rem",
              }}
            >
              <LogOut style={{ width: "1rem", height: "1rem" }} />
              Sign Out
            </button>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div style={{ minHeight: "100vh", display: "flex", width: "100%" }}>
      {!isMobile && (
        <aside style={{
          display: "flex",
          flexDirection: "column",
          width: "14rem",
          borderRight: "1px solid hsl(var(--sidebar-border))",
          background: "hsl(var(--sidebar))",
          flexShrink: 0,
          height: "100vh",
          position: "sticky",
          top: 0,
          overflowY: "auto",
        }}>
          <SidebarContent />
        </aside>
      )}

      {mobileOpen && (
        <>
          <div
            onClick={() => setMobileOpen(false)}
            style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 40 }}
          />
          <div style={{
            position: "fixed",
            top: 0,
            left: 0,
            bottom: 0,
            width: "14rem",
            background: "hsl(var(--sidebar))",
            borderRight: "1px solid hsl(var(--sidebar-border))",
            zIndex: 50,
            display: "flex",
            flexDirection: "column",
          }}>
            <div style={{ display: "flex", justifyContent: "flex-end", padding: "0.75rem" }}>
              <Button variant="ghost" size="icon" onClick={() => setMobileOpen(false)}>
                <X style={{ width: "1.25rem", height: "1.25rem" }} />
              </Button>
            </div>
            <SidebarContent />
          </div>
        </>
      )}

      <div style={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0 }}>
        {isMobile && (
          <header style={{
            height: "3.5rem",
            borderBottom: "1px solid hsl(var(--border))",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "0 1rem",
            background: "hsl(var(--card))",
            position: "sticky",
            top: 0,
            zIndex: 10,
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
              <ShieldCheck style={{ width: "1.25rem", height: "1.25rem", color: "hsl(var(--primary))" }} />
              <span style={{ fontWeight: 600, fontSize: "1rem" }}>Admin Panel</span>
            </div>
          </header>
        )}

        <main style={{ flex: 1, padding: isMobile ? "1rem" : "2rem", overflowY: "auto", paddingBottom: isMobile ? "5rem" : undefined }}>
          <div style={{ maxWidth: "72rem", margin: "0 auto" }}>
            {children}
          </div>
        </main>

        {isMobile && (
          <button
            onClick={() => setMobileOpen(true)}
            aria-label="Open menu"
            style={{
              position: "fixed",
              bottom: "1.5rem",
              left: "1.5rem",
              zIndex: 30,
              width: "3.25rem",
              height: "3.25rem",
              borderRadius: "9999px",
              background: "hsl(var(--primary))",
              color: "hsl(var(--primary-foreground))",
              border: "none",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 4px 16px rgba(0,0,0,0.25)",
            }}
          >
            <Menu style={{ width: "1.375rem", height: "1.375rem" }} />
          </button>
        )}
      </div>
    </div>
  );
}
